import React, { useState } from 'react';
import axios from 'axios';
import './KatalogSuchePanel.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface Props {
  onUebernommen?: () => void;
}

interface KatalogTreffer {
  partner_id: number;
  partner_name: string;
  artikelnummer: string;
  kurztext1: string | null;
  kurztext2: string | null;
  mengeneinheit: string | null;
  listenpreis: number | null;
  nettopreis: number | null;
  ean: string | null;
  matchcode: string | null;
}

interface PartnerOption {
  partner_id: number;
  name: string;
}

interface UebernahmeResponse {
  artikel_id: number;
  bezeichnung: string;
  neu_angelegt: boolean;
}

interface KatalogSucheResponse {
  treffer: KatalogTreffer[];
  gesamt: number;
}

type ZeilenStatus = { neu_angelegt: boolean } | 'error';

const SUCHE_LIMIT = 50;

const zeilenKey = (t: KatalogTreffer) => `${t.partner_id}::${t.artikelnummer}`;

const formatPreis = (wert: number | null): string => {
  if (wert === null || wert === undefined) return '–';
  return wert.toFixed(4).replace('.', ',');
};

const KatalogSuchePanel: React.FC<Props> = ({ onUebernommen }) => {
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const [partnerId, setPartnerId] = useState<string>('');
  const [lieferanten, setLieferanten] = useState<PartnerOption[]>([]);
  const [lieferantenGeladen, setLieferantenGeladen] = useState(false);
  const [ergebnisse, setErgebnisse] = useState<KatalogTreffer[]>([]);
  const [gesamt, setGesamt] = useState(0);
  const [loading, setLoading] = useState(false);
  const [ladeMehr, setLadeMehr] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [uebernommen, setUebernommen] = useState<Record<string, ZeilenStatus>>({});
  const [uebernahmeLaeuft, setUebernahmeLaeuft] = useState<string | null>(null);
  const [hatGesucht, setHatGesucht] = useState(false);

  const ladeLieferanten = async () => {
    if (lieferantenGeladen) return;
    try {
      const response = await axios.get(`${API_URL}/api/partners?typ=Lieferant`);
      setLieferanten(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Lieferanten:', error);
    } finally {
      setLieferantenGeladen(true);
    }
  };

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) {
      ladeLieferanten();
    }
  };

  const sucheAusfuehren = async (offset: number, anhaengen: boolean) => {
    if (!query.trim()) return;
    if (anhaengen) setLadeMehr(true); else setLoading(true);
    setErrorMsg(null);
    try {
      const params = new URLSearchParams();
      params.append('q', query.trim());
      if (partnerId) params.append('partner_id', partnerId);
      params.append('limit', String(SUCHE_LIMIT));
      params.append('offset', String(offset));
      const response = await axios.get<KatalogSucheResponse>(`${API_URL}/api/kataloge/suche?${params}`);
      setErgebnisse((prev) => (anhaengen ? [...prev, ...response.data.treffer] : response.data.treffer));
      setGesamt(response.data.gesamt);
    } catch (error: any) {
      if (!anhaengen) setErgebnisse([]);
      setErrorMsg(error?.response?.data?.detail ?? 'Suche fehlgeschlagen.');
    } finally {
      setHatGesucht(true);
      setLoading(false);
      setLadeMehr(false);
    }
  };

  const sucheStarten = () => sucheAusfuehren(0, false);
  const mehrLaden = () => sucheAusfuehren(ergebnisse.length, true);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      sucheStarten();
    }
  };

  const artikelUebernehmen = async (treffer: KatalogTreffer) => {
    const key = zeilenKey(treffer);
    setUebernahmeLaeuft(key);
    setErrorMsg(null);
    try {
      const response = await axios.post<UebernahmeResponse>(
        `${API_URL}/api/kataloge/artikel-uebernehmen`,
        { partner_id: treffer.partner_id, artikelnummer: treffer.artikelnummer }
      );
      setUebernommen((prev) => ({ ...prev, [key]: { neu_angelegt: response.data.neu_angelegt } }));
      if (onUebernommen) onUebernommen();
    } catch (error: any) {
      setUebernommen((prev) => ({ ...prev, [key]: 'error' }));
      setErrorMsg(error?.response?.data?.detail ?? 'Übernahme fehlgeschlagen.');
    } finally {
      setUebernahmeLaeuft(null);
    }
  };

  return (
    <div className="ksp-panel">
      <button
        type="button"
        className="ksp-toggle"
        onClick={toggleExpanded}
        aria-expanded={expanded}
      >
        <span className="ksp-toggle-icon">{expanded ? '▼' : '▶'}</span>
        Im Großhandelskatalog suchen
      </button>

      {expanded && (
        <div className="ksp-body">
          <div className="ksp-suchleiste">
            <input
              type="text"
              className="ksp-suchfeld"
              placeholder="Artikelnummer, Bezeichnung oder EAN..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <select
              className="ksp-lieferanten-select"
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
            >
              <option value="">Alle Lieferanten</option>
              {lieferanten.map((l) => (
                <option key={l.partner_id} value={l.partner_id}>{l.name}</option>
              ))}
            </select>
            <button
              type="button"
              className="ksp-btn ksp-btn-primary"
              onClick={sucheStarten}
              disabled={loading || !query.trim()}
            >
              {loading ? 'Sucht…' : 'Suchen'}
            </button>
          </div>

          {errorMsg && <div className="ksp-fehler">{errorMsg}</div>}

          {hatGesucht && ergebnisse.length > 0 && (
            <div className="ksp-trefferzaehler">{ergebnisse.length} von {gesamt} Treffern</div>
          )}

          {ergebnisse.length > 0 && (
            <div className="ksp-table-wrap">
              <table className="ksp-table">
                <thead>
                  <tr>
                    <th>Händler</th>
                    <th>Artikelnr.</th>
                    <th>Bezeichnung</th>
                    <th>Einheit</th>
                    <th>Listenpreis</th>
                    <th>Nettopreis</th>
                    <th>EAN</th>
                    <th>Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {ergebnisse.map((treffer) => {
                    const key = zeilenKey(treffer);
                    const status = uebernommen[key];
                    const bezeichnung = [treffer.kurztext1, treffer.kurztext2]
                      .filter((t) => t && t.trim())
                      .join(' ');
                    return (
                      <tr key={key}>
                        <td>{treffer.partner_name}</td>
                        <td>{treffer.artikelnummer}</td>
                        <td>{bezeichnung || '–'}</td>
                        <td>{treffer.mengeneinheit || '–'}</td>
                        <td>{formatPreis(treffer.listenpreis)}</td>
                        <td>{formatPreis(treffer.nettopreis)}</td>
                        <td>{treffer.ean || '–'}</td>
                        <td>
                          {status && status !== 'error' ? (
                            <span className="ksp-status-ok">
                              {status.neu_angelegt ? '✓ übernommen (neu angelegt)' : '✓ bereits vorhanden'}
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="ksp-btn ksp-btn-uebernehmen"
                              onClick={() => artikelUebernehmen(treffer)}
                              disabled={uebernahmeLaeuft === key}
                            >
                              {uebernahmeLaeuft === key ? 'Übernimmt…' : 'Als Artikel übernehmen'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {ergebnisse.length < gesamt && (
            <div className="ksp-mehr-laden">
              <button
                type="button"
                className="ksp-btn"
                onClick={mehrLaden}
                disabled={ladeMehr}
              >
                {ladeMehr ? 'Lädt…' : `Mehr laden (${gesamt - ergebnisse.length} weitere)`}
              </button>
            </div>
          )}

          {!loading && ergebnisse.length === 0 && (
            <div className="ksp-leer">
              {hatGesucht ? 'Keine Treffer gefunden.' : 'Noch keine Suche durchgeführt.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KatalogSuchePanel;
